import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import Footer from "@/components/Footer";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Bienvenida");
      navigate(res.user?.role === "admin" ? "/super-admin" : "/dashboard");
    } else {
      setError(res.error);
    }
  };

  const useDemo = () => {
    setEmail("demo@grupodisfrutaparama.com");
    setPassword("Demo1234!");
  };

  return (
    <div className="min-h-screen bg-void text-neutral-100 grain flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-10 justify-center" data-testid="brand-link">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-neonpink flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-void" strokeWidth={2} />
            </div>
            <span className="font-display text-3xl">AgendaPro</span>
          </Link>

          <div className="bg-surface gold-border rounded-2xl p-8 animate-fade-up">
            <p className="overline mb-2">Bienvenida de vuelta</p>
            <h1 className="font-display text-3xl mb-6">Inicia sesión</h1>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-neutral-300 text-xs uppercase tracking-widest">Correo</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tucorreo@negocio.com"
                  required
                  data-testid="login-email-input"
                  className="mt-2 bg-void border-gold/20 focus:border-cyan focus-visible:ring-1 focus-visible:ring-cyan h-12"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-neutral-300 text-xs uppercase tracking-widest">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="mt-2 bg-void border-gold/20 focus:border-cyan focus-visible:ring-1 focus-visible:ring-cyan h-12"
                />
              </div>

              {error && (
                <div data-testid="login-error" className="text-neonpink text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full bg-gold text-void hover:bg-gold/90 rounded-full font-bold h-12 mt-2"
              >
                {loading ? "Entrando…" : "Entrar"}
              </Button>

              <button
                type="button"
                onClick={useDemo}
                data-testid="use-demo-btn"
                className="w-full text-center text-xs uppercase tracking-widest text-cyan hover:text-gold transition-colors duration-200 mt-2"
              >
                Usar cuenta demo
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-neutral-400">
              ¿Aún no tienes cuenta?{" "}
              <Link to="/register" className="text-gold hover:underline" data-testid="go-register-link">
                Regístrate
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
